/**
 * BPA Solutions - Phone OTP API Layer (Surgical Fix)
 * Handles Phone Verification Demo independently of Email Verification.
 */

const GAS_URL = import.meta.env.VITE_SMS_OTP_GAS_WEB_APP_URL;
const IS_DEMO_MODE = import.meta.env.VITE_PHONE_OTP_DEMO_MODE === 'true';

const getDeviceId = () => {
  let deviceId = localStorage.getItem('bpa_phone_demo_device_id');
  if (!deviceId) {
    deviceId = `dev_${Math.random().toString(36).slice(2, 11)}_${Date.now()}`;
    localStorage.setItem('bpa_phone_demo_device_id', deviceId);
  }
  return deviceId;
};

const getContext = () => ({
  sourceApp: "smart_forms_phone_demo",
  deviceId: getDeviceId(),
  pageUrl: window.location.href,
  userAgent: navigator.userAgent
});

const callPhoneBackend = async (payload) => {
  // Diagnostic Logging (No OTP codes logged)
  const host = GAS_URL ? new URL(GAS_URL).hostname : 'NOT_CONFIGURED';
  console.log(`[PHONE API DIAGNOSTIC] Action: ${payload.action}`, {
    hasUrl: !!GAS_URL,
    host: host,
    demoMode: IS_DEMO_MODE
  });

  // Fail-closed logic
  if (!GAS_URL) {
    if (IS_DEMO_MODE) {
      return mockPhoneFallback(payload);
    }
    throw new Error('Phone OTP backend is not configured. Add VITE_SMS_OTP_GAS_WEB_APP_URL and redeploy.');
  }

  try {
    const body = new URLSearchParams();
    const fullPayload = { ...payload, ...getContext() };
    
    Object.keys(fullPayload).forEach(key => {
      body.append(key, fullPayload[key] || '');
    });

    const response = await fetch(GAS_URL, { method: 'POST', body });
    console.log(`[PHONE API DIAGNOSTIC] HTTP Status: ${response.status}`);
    
    const text = await response.text();
    let result;
    
    try {
      result = JSON.parse(text);
    } catch (e) {
      console.error(`[PHONE API ERROR] JSON Parse Failed. Raw response: ${text.substring(0, 300)}`);
      throw new Error(`Invalid Backend Response: Not a JSON object.`);
    }

    console.log(`[PHONE API DIAGNOSTIC] Parsed Result:`, { ...result, code: 'REDACTED' });

    // Strict success check
    if (result.ok === true) {
      return result;
    }
    
    throw new Error(result.message || result.error || 'Action failed');
  } catch (error) {
    console.error(`[PHONE API ERROR] Action: ${payload.action}`, error);
    throw error;
  }
};

const mockPhoneFallback = async (payload) => {
  await new Promise(r => setTimeout(r, 1000));
  console.warn(`[PHONE MOCK WARNING] Running in Demo Mode - No real SMS sent.`);

  switch (payload.action) {
    case 'send_sms_otp':
      return { ok: true, message: "Demo mode: Code is 123456" };
    case 'verify_sms_otp':
      if (payload.code === '123456') return { ok: true, verifiedAt: new Date().toISOString() };
      throw new Error('Invalid code. Use 123456 for demo mode.');
    case 'submit_phone_form':
      return { ok: true, submissionId: `DEMO-SMS-${Math.floor(Math.random() * 900000) + 100000}` };
    default:
      throw new Error('Unknown phone action');
  }
};

export const phoneOtpApi = {
  sendSmsOtp: (phone) => callPhoneBackend({ action: "send_sms_otp", phone }),
  verifySmsOtp: (phone, code) => callPhoneBackend({ action: "verify_sms_otp", phone, code }),
  submitForm: (payload) => callPhoneBackend({ action: "submit_phone_form", ...payload })
};